import { useAnnexes } from '../hooks'

/**
 * Small badge rendered next to a field label when one or more published annexes
 * ever touched that field. Shows the latest annex number; hover/title carries the
 * count + old→new pair from the most recent change. Renders nothing when the field
 * was never annexed.
 *
 * Backed by the same {@code useAnnexes} cache that the AnnexesTab / banner use,
 * so this is free additional surface on already-loaded data.
 */
export function FieldAnnexMarker({
  contractId,
  fieldCode,
}: {
  contractId: number
  fieldCode: string
}) {
  const { data } = useAnnexes(contractId, false)
  if (!data) return null

  const touches = data
    .filter(a => a.status === 'PUBLISHED' && (a.changes ?? []).some(c => c.fieldCode === fieldCode))
    .sort((a, b) => (b.annexNumber ?? 0) - (a.annexNumber ?? 0))

  if (touches.length === 0) return null

  const latest = touches[0]
  const latestChange = (latest.changes ?? []).find(c => c.fieldCode === fieldCode)
  const tooltip = latestChange
    ? `Zmienione aneksem #${latest.annexNumber} (${fmtCell(latestChange.oldValue)} → ${fmtCell(latestChange.newValue)})${touches.length > 1 ? ` · łącznie ${touches.length} aneksów` : ''}`
    : `Zmienione aneksem #${latest.annexNumber}`

  return (
    <span
      className="inline-flex items-center gap-0.5 px-1.5 py-px rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 align-middle"
      title={tooltip}
    >
      <span className="size-1 rounded-full bg-amber-500" aria-hidden />
      A{latest.annexNumber}
    </span>
  )
}

function fmtCell(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'boolean') return v ? 'Tak' : 'Nie'
  if (Array.isArray(v)) return v.join(', ')
  return String(v)
}
